import React, { useState } from 'react';
import { Store } from 'pullstate';
import Dialog from '@mui/material/Dialog';
import ShareIcon from '@mui/icons-material/Share';
import { AppStore } from '../../../../AppStore';
import { DESKTOP_SHARE_TAG, MOBILE_SHARE_TAG, PuzzleShareCodes, PuzzleStoreBase } from '../../../../utils/types';
import { PuzzleNames, PuzzleNamesForUI } from '../../../../utils/types';
import { formatDuration } from '../utils';
import { isMobile } from 'react-device-detect';
import './StatsDialog.less';

type StatsDialogProps = {
  puzzleName: PuzzleNames;
  store: Store<PuzzleStoreBase>;
};

const getScheduleText = (puzzleName: PuzzleNames) => {
  switch (puzzleName) {
    case PuzzleNames.Megarubigram:
      return "Megarubigram updates on Tuesdays and Fridays.";
    case PuzzleNames.Pathword5x5:
      return "Pathword 5x5 updates on Tuesdays and Fridays.";
    case PuzzleNames.Morphology:
      return "New Morphology puzzles every weekday.";
    case PuzzleNames.Ultrarubigram:
      return "Ultrarubigram updates on the first of each month.";
    default:
      return "New puzzle daily.";
  }
};

const StatsDialog = ({ puzzleName, store }: StatsDialogProps) => {
  const { statsDialogIsOpen, solved, time, revealed, completed, puzzleId } = store.useState();
  const [showClipboardAlert, setShowClipboardAlert] = useState(false);

  const userData = AppStore.useState(s => s.userData);
  const puzzleStats = userData?.stats[puzzleName];

  const todaysTimeFormatted = formatDuration(time);

  const numPlayed = puzzleStats?.numPlayed || 0;
  const numSolved = puzzleStats?.numSolved || 0;
  const percentWins = Math.round((numSolved / numPlayed) * 100) || 0;
  const currentStreak = puzzleStats?.currentStreak;
  const bestTime = puzzleStats?.bestTime || 0;
  const averageTime = puzzleStats?.averageTime || 0;
  const numUnder10 = puzzleStats?.under10 ?? null;
  const numUnder30 = puzzleStats?.under30 ?? null;
  const numUnder60 = puzzleStats?.under60 ?? null;
  const numUnder120 = puzzleStats?.under120 ?? null;
  const numUnder300 = puzzleStats?.under300 ?? null;
  const numUnder600 = puzzleStats?.under600 ?? null;
  const numUnder1200 = puzzleStats?.under1200 ?? null;
  const numUnder2400 = puzzleStats?.under2400 ?? null;
  const numUnder3600 = puzzleStats?.under3600 ?? null;

  const onClose = () => {
    store.update(s => {
      s.statsDialogIsOpen = false;
      s.isSoftPaused = false;
    });
  };

  const onShare = () => {
    const shareCode = isMobile ? PuzzleShareCodes[puzzleName + MOBILE_SHARE_TAG]
      : PuzzleShareCodes[puzzleName + DESKTOP_SHARE_TAG];
    const puzzleUrl = `https://dailybaffle.com/${puzzleName}?utm_source=${shareCode}`;

    let resultText = `I solved ${PuzzleNamesForUI[puzzleName]} #${puzzleId} in ${todaysTimeFormatted}!`;

    if (navigator.share && isMobile) {
      navigator.share({ text: resultText, url: puzzleUrl });
    } else {
      resultText = `${resultText}\n${puzzleUrl}`;
      navigator.clipboard.writeText(resultText);
      setShowClipboardAlert(true);
      setTimeout(() => {
        setShowClipboardAlert(false);
      }, 1000);
    }
  };

  const createAccount = () => {
    AppStore.update(s => { s.signInDialog.isOpen = true; });
    setTimeout(() => {
      onClose();
    }, 100);
  };

  const shouldShowShare = [PuzzleNames.Morphology, PuzzleNames.Arithmeglyphs].includes(puzzleName)
    ? completed : (solved || revealed);

  return ( 
    <>
      <Dialog onClose={onClose} open={statsDialogIsOpen}>
        <div className="statsDialogContent">
          {showClipboardAlert &&
            <div className="clipboardAlert">
              Copied results to clipboard
            </div>
          }
          <h1>{PuzzleNamesForUI[puzzleName]} Stats</h1>
            {solved ?
              <>
                <p className="topStatsMessage">
                  {`You solved today's puzzle in ${todaysTimeFormatted}. Nice work!`}
                </p>
                <p className="bottomStatsMessage">
                  {getScheduleText(puzzleName)}
                </p>
              </>
              :
              <>
                <p className="topStatsMessage">
                  {revealed ? `Today's puzzle was revealed.` : `You haven't solved today's puzzle yet.`}
                </p>
                {revealed && <p className="bottomStatsMessage">
                  {getScheduleText(puzzleName)}
                </p>}
              </>
            }

          <div className="statsDisplayContainer">
            {!userData &&
              <div className="signedOutStatsCover">
                <div className="signedOutMessage">
                  <span onClick={createAccount}>Create a free account</span> to keep track of your daily stats!
                </div>
              </div>
            }
            <div className="topStatsRow">
              <div>
                <div className="value">{numPlayed}</div>
                <div className="label">Played</div>
              </div>
              <div>
                <div className="value">{numSolved}</div>
                <div className="label">Solved</div>
              </div>
              <div>
                <div className="value">{percentWins}%</div>
                <div className="label">Solve Rate</div>
              </div>
            </div>

            <div className="middleStatsRow">
              <div>
                <div className="value">{currentStreak}</div>
                <div className="label">Solve Streak</div>
              </div>
              <div>
                <div className="value">{formatDuration(averageTime)}</div>
                <div className="label">Average Time</div>
              </div>
              <div>
                <div className="value">{formatDuration(bestTime)}</div>
                <div className="label">Best Time</div>
              </div>
            </div>

            <h2>Solve Times</h2>

            <div className="bottomStatsRow">
            {numUnder10 !== null && (
              <div>
                <div className="value">{numUnder10}</div>
                <div className="label">&lt;10s</div>
              </div>
            )}
            {numUnder30 !== null && (
              <div>
                <div className="value">{numUnder30}</div>
                <div className="label">&lt;30s</div>
              </div>
            )}
            {numUnder60 !== null && (
              <div>
                <div className="value">{numUnder60}</div>
                <div className="label">&lt;60s</div>
              </div>
            )}
            {numUnder120 !== null && (
              <div>
                <div className="value">{numUnder120}</div>
                <div className="label">&lt;2m</div>
              </div>
            )}
            {numUnder300 !== null && (
              <div>
                <div className="value">{numUnder300}</div>
                <div className="label">&lt;5m</div>
              </div>
            )}
            {numUnder600 !== null && (
              <div>
                <div className="value">{numUnder600}</div>
                <div className="label">&lt;10m</div>
              </div>
            )}
            {numUnder1200 !== null && (
              <div>
                <div className="value">{numUnder1200}</div>
                <div className="label">&lt;20m</div>
              </div>
            )}
            {numUnder2400 !== null && (
              <div>
                <div className="value">{numUnder2400}</div>
                <div className="label">&lt;40m</div>
              </div>
            )}
            {numUnder3600 !== null && (
              <div>
                <div className="value">{numUnder3600}</div>
                <div className="label">&lt;1h</div>
              </div>
            )}
            </div>
          </div>

          <div className="statsButtonsContainer">
            <button onClick={onClose}>View Puzzle</button>
            {shouldShowShare &&
              <button className="shareButton" onClick={onShare}>
                <span>Share</span>
                <ShareIcon/>
              </button>
            }
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default StatsDialog;